import { CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { WalletDataService } from 'src/app/services/data/wallet-data.service';
import { IndicatorDataService } from 'src/app/services/data/indicator-data.service';
import { PersonType } from 'src/app/services/models/enums';
import { PageLoaderService } from 'src/app/services/page-loader.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogProps,
  ConfirmDialogResult,
  ConfirmDialogResultType,
} from 'src/app/shared/components/modals/confirmation-dialog/confirmation-dialog.component';
import { InputRegexDirective } from 'src/app/shared/directives/input-regex.directive';
import { MaxlengthEnforcerDirective } from 'src/app/shared/directives/maxlength-enforcer.directive';
import {
  PlanLightResponseDto,
  PlanService,
} from 'src/generated-sources/openapi/configuration-service/plan';
import {
  PlanDto,
  PlanIndicatorFindByPlanIdResponseDto,
  PlanIndicatorResponseDto,
  PlanIndicatorService,
} from 'src/generated-sources/openapi/configuration-service/plan-indicator';
import {
  CreateWalletResponseDto,
  UpdateWalletResponseDto,
  WalletService,
} from 'src/generated-sources/openapi/wallet-service/wallet';
import { IconComponent } from '../../icon/icon.component';
import { toolTipPosstions } from './portfolio-manager-tool-tip-positions';
import { AppConfigService } from 'src/app/services/app-config.service';
import { ProfileDataService } from 'src/app/services/data/profile-data.service';
import { createEmptyWallet, Wallet } from 'src/app/services/models/wallet-service/wallet.model';
import {
  AllPlanResponseDto,
  ClientPlanService,
} from 'src/generated-sources/openapi/configuration-service/client-plan';
import { isAlert30DaysWallet } from 'src/app/helpers/plan-utils';

type WalletDialogProps = Wallet;

export interface IWalletDialogResponse {
  data?: any;
  action: ConfirmDialogResultType;
}

@Component({
  selector: 'app-portfolio-manager',
  templateUrl: './portfolio-manager.component.html',
  styleUrls: ['./portfolio-manager.component.scss'],
  imports: [
    MatDialogModule,
    MatIconModule,
    FormsModule,
    MatButtonModule,
    ConfirmationDialogComponent,
    IconComponent,
    InputRegexDirective,
    MaxlengthEnforcerDirective,
    OverlayModule
],
})
export class PortfolioManagerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('nameInput') nameInput?: ElementRef;
  private readonly nameChangeSubject = new Subject<string>();
  @ViewChild('originOverlay', { static: false, read: CdkOverlayOrigin })
  originOverlay?: CdkOverlayOrigin;

  private readonly destroy$ = new Subject<void>();

  private readonly MIN_NAME_LENGTH = 3;
  private readonly MAX_NAME_LENGTH = 50;
  public readonly NAME_REGEX = '^[a-zA-Z0-9_\\s]*$';

  wallet: Wallet = createEmptyWallet();

  selectedValue?: string;

  priorName = '';
  nameAlreadyExists = false;
  isVerifyingName = false;
  originalWallet: Wallet = createEmptyWallet();
  maxMinLength: boolean = false;
  personType = PersonType.COMPANY;
  selectedPlan: PlanDto = { id: '', name: '', description: '' };
  plans: PlanLightResponseDto[] = [];
  planIndicators: PlanIndicatorResponseDto[] = [];
  planIndicatorsMap = new Map<string, PlanIndicatorResponseDto[]>();
  showPlanTooltip = false;
  showPlanIcon = false;

  toolTipPosstions = toolTipPosstions;

  flagEnablePlans = false;

  constructor(
    private readonly appConfig: AppConfigService,
    private readonly walletService: WalletService,
    private readonly walletDataService: WalletDataService,
    private readonly indicatorDataService: IndicatorDataService,
    private readonly pageLoaderService: PageLoaderService,
    private readonly snackbarService: SnackbarService,
    private readonly clientPlanService: ClientPlanService,
    private readonly planService: PlanService,
    private readonly planIndicatorService: PlanIndicatorService,
    private readonly profileDataService: ProfileDataService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly dialogRef: MatDialogRef<PortfolioManagerComponent, IWalletDialogResponse>,
    @Inject(MAT_DIALOG_DATA) private readonly data: WalletDialogProps,
  ) {
    if (!data) return;

    this.wallet = data;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.nameInput?.nativeElement.focus();
    }, 200);
  }

  ngOnInit() {
    this.flagEnablePlans = this.appConfig.config.featureToggles.enablePlans;
    if (this.flagEnablePlans) {
      this.loadInitialData();
    } else if (!this.isCreating) {
      this.originalWallet = { ...this.wallet };
    }

    this.dialogRef.beforeClosed().subscribe((value) => {
      const isEditing = !this.isCreating;
      const didNotConfirm = value?.action !== ConfirmDialogResult.Confirm;

      if (isEditing && didNotConfirm) {
        this.cancelChanges();
      }
    });

    this.nameChangeSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((name) => this.verifyNameExistsObservable(name)),
        takeUntil(this.destroy$),
      )
      .subscribe((alreadyExists) => {
        if (alreadyExists) {
          this.snackbarService.error($localize`El nombre ya existe`);
          this.nameAlreadyExists = true;
        }
        this.isVerifyingName = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get someInvalidData() {
    const emptyName = !this.currentName;
    const invalidNameLength =
      this.currentName.length < this.MIN_NAME_LENGTH ||
      this.currentName.length > this.MAX_NAME_LENGTH;
    const emptyPlan = this.flagEnablePlans && !this.wallet?.planId;

    return (
      emptyName || invalidNameLength || this.nameAlreadyExists || this.isVerifyingName || emptyPlan
    );
  }

  get isCreating() {
    return !this.wallet?.id;
  }

  get currentName() {
    return (this.wallet?.name || '').toLowerCase().trim();
  }

  get originalName() {
    return (this.originalWallet?.name || '').toLowerCase().trim();
  }

  get hasAnyChange() {
    const nameChanges = this.wallet.name !== this.originalWallet.name;
    const favoriteChanges = this.wallet.favorite !== this.originalWallet.favorite;
    return nameChanges || favoriteChanges;
  }

  onNameChange(event: InputEvent) {
    this.verifyMaxMinLength();
    this.isVerifyingName = true;
    this.nameAlreadyExists = false;

    const typedValue = (event.target as HTMLInputElement).value || '';
    this.nameChangeSubject.next(typedValue);
  }

  verifyMaxMinLength() {
    if (!this.currentName) {
      this.wallet.name = this.wallet?.name.substring(0, this.wallet.name.length - 1) || '';
    }

    this.wallet.name = this.wallet.name?.trimStart();
    this.maxMinLength = true;

    if (!this.currentName || this.currentName.length < 3 || this.currentName.length > 50) {
      this.maxMinLength = false;
    }
  }

  verifyNameExistsObservable(typedValue: string): Observable<boolean> {
    const currentName = typedValue.toLowerCase().trim();
    const originalName = (this.originalWallet?.name || '').toLowerCase().trim();

    if (!currentName || currentName === originalName) {
      return of(false);
    }

    const alreadyExists = this.walletDataService
      .getWalletsValue()
      .some((wallet) => wallet.name.toLowerCase() === currentName);

    return of(alreadyExists).pipe(delay(100));
  }

  toggleFavorite() {
    this.wallet.favorite = !this.wallet.favorite;
  }

  handleSubmit() {
    if (this.someInvalidData) return;

    this.pageLoaderService.setPageLoader(true);

    if (this.isCreating) {
      this.createPortfolio();
    } else {
      this.updatePortfolio();
    }
  }

  async createPortfolio() {
    const { name, favorite, planId } = this.wallet;
    const createWalletRequestDto = { name: name.trim(), favorite, planId };

    this.walletService.create({ createWalletRequestDto }).subscribe({
      next: (response: CreateWalletResponseDto) => {
        this.snackbarService.normal($localize`Cartera creada correctamente`);
        this.dialogRef.close({
          data: response?.id,
          action: ConfirmDialogResult.Confirm,
        });
        this.pageLoaderService.setPageLoader(false);
      },
      error: (error) => this.handleApiError(error),
    });
  }

  updatePortfolio() {
    const { name, favorite } = this.wallet;
    const updateWalletRequestDto = { name: name.trim(), favorite };

    this.walletService.updateById({ id: this.wallet.id, updateWalletRequestDto }).subscribe({
      next: (response: UpdateWalletResponseDto) => {
        this.snackbarService.normal($localize`Cartera actualizada correctamente`);
        this.dialogRef.close({
          data: this.wallet,
          action: ConfirmDialogResult.Confirm,
        });
        this.walletDataService.updateWallet(this.wallet);
        this.pageLoaderService.setPageLoader(false);
      },
      error: (error) => this.handleApiError(error),
    });
  }

  tryError(errorStatus?: number) {
    if (errorStatus === 401) {
      this.snackbarService.error(
        $localize`No se pudo realizar la acción porque no tiene el perfil autorizado. Consultar con su administrador. Estatus ` +
          errorStatus,
      );
    } else {
      let errorMessage = this.isCreating
        ? $localize`:@@createWalletError:Error al agregar la cartera`
        : $localize`:@@updateWalletError:Error al actualizar la cartera`;

      this.snackbarService.error(errorMessage);
    }
  }

  cancelChanges() {
    this.wallet.name = this.originalWallet.name;
    this.wallet.favorite = this.originalWallet.favorite;
  }

  get confirmationDialogProps(): ConfirmationDialogProps {
    return {
      title: this.isCreating
        ? $localize`:@@createWallet:Incluir Cartera`
        : $localize`:@@editWallet:Editar Cartera`,
      cancelButton: {
        text: $localize`:@@deleteDocumentBtnCancel:Cancel`,
      },
      confirmButton: {
        text: this.isCreating ? $localize`:@@create:Crear` : $localize`:@@accept:Aceptar`,
        action: () => this.handleSubmit(),
        disabled: this.someInvalidData || !this.hasAnyChange,
      },
    };
  }

  selectPlan(plan: PlanDto) {
    const indicators = this.planIndicatorsMap.get(plan.id);
    if (indicators) {
      this.wallet.planId = plan.id;
      this.planIndicators = indicators;
    } else {
      this.findIndicatorsForPlan(plan);
    }
  }

  private findIndicatorsForPlan(plan: PlanDto) {
    this.planIndicatorService
      .findByPlanId({ planId: plan.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PlanIndicatorFindByPlanIdResponseDto) => {
          this.planIndicatorsMap.set(plan.id, response.items);
          this.updateComponentState(plan, response.items);
        },
        error: (error) => this.handleApiError(error),
      });
  }

  private loadInitialData() {
    if (!this.isCreating) {
      this.loadUpdateData();
    } else {
      this.loadCreateData();
    }
  }

  private loadCreateData() {
    const clientId = this.profileDataService.currentProfile?.clientId;

    if (clientId) {
      this.clientPlanService.findActivePlansByClientId({ clientId }).subscribe({
        next: (response: AllPlanResponseDto) => {
          const filtered = (response?.items ?? []).filter((plan) => !isAlert30DaysWallet(plan.id));
          if (filtered.length > 0) {
            this.plans = filtered;
          } else {
            this.snackbarService.error(
              $localize`:@@plansNotFound:No hay planes disponibles. Consulte a su administrador para más información.`,
            );
          }
        },
        error: (error) => this.handleApiError(error),
      });
    }
  }

  private loadUpdateData() {
    this.originalWallet = { ...this.wallet };
    this.priorName = this.currentName;
    this.verifyMaxMinLength();
    this.findPlanIndicators();
  }

  private findPlanIndicators() {
    const planId = this.originalWallet.planId;
    this.planIndicatorService.findByPlanId({ planId }).subscribe({
      next: (response) => {
        if (response.items.length > 0) {
          const plan = response.items[0].plan;
          this.updateComponentState(plan, response.items);
        } else {
          this.findPlanById();
        }
      },
      error: (error) => this.handleApiError(error),
    });
  }

  private findPlanById() {
    const planId = this.originalWallet.planId;
    this.planService.findById({ id: planId }).subscribe({
      next: (planResponse) => {
        this.updateComponentState(planResponse);
      },
      error: (error) => this.handleApiError(error),
    });
  }

  private updateComponentState(plan: PlanDto, indicators: PlanIndicatorResponseDto[] = []) {
    this.selectedPlan = plan;
    this.planIndicators = this.indicatorDataService.sortByDisplayOrder(
      indicators,
      (pi) => pi.indicator.id,
    );
    this.wallet.planId = plan.id;
    this.showPlanIcon = true;
    if (!this.isCreating) {
      this.plans = [plan];
    }

    this.cdRef.detectChanges();
  }

  private handleApiError(error: any): void {
    this.tryError(error?.status);
    this.pageLoaderService.setPageLoader(false);
  }
}
